import {
  Alert,
  Button,
  Grid,
  Snackbar,
  SvgIcon,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useContext, useEffect, useState } from 'react';
import RecipeList from './RecipeList';
import { Search } from '@mui/icons-material';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import CustomPagination from '../../../../components/forms/Pagination';
import {
  CreateMadeInStoreItemRequestDto,
  ItemComponent,
  MadeInStoreType,
  Recipe,
  UpsertItemComponent,
} from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../../components/shared/useUser';
import useLogError from '../../../../hooks/useLogError';
import CreateRecipeDialog from '../CreateRecipeDialog';
import { useRecipeDialog } from '../../../../pages/Recipe/context/RecipeDialogContext';
import CreateBulkManage from '../CreateBulkManage';
import { Section } from '../../../../components/ui/Section';
import { handleSearchFieldKeyDown } from '../../../../utils';

type Props = {
  currencyCode?: string;
  onCreateNew: () => void;
}

const RecipesTab: FC<Props> = ({ currencyCode, onCreateNew }) => {
  const { t } = useTranslation('recipe');
  const { user, selectedFranchise, selectedStore, hasPermissionTo } = useContext(UserContext);
  const {
    getRecipes,
    exportRecipes,
    createMadeInStore,
    addComponent,
    deleteComponent,
  } = useNswagClient();
  const theme = useTheme();
  const { logError } = useLogError();
  const { setIsEditMode, setShowRecipeDialog } = useRecipeDialog();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [lastSearchTerm, setLastSearchTerm] = useState<string>('');

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(1);
  const [showSubmitStatusMessage, setShowSubmitStatusMessage] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openBulk, setOpenBulk] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successMessageBulk, setSuccessMessageBulk] = useState('');
  const [showUploadSuccessMessage, setShowUploadSuccessMessage] = useState(false);

  const isCustomMessageFranchise = (): boolean => {
    const allowedFranchises = ['NE Apple Green GB', 'Pasta Evangelists UK', 'Insomnia Cookies UK', 'Chick-fil-A Singapore', 'Chick-fil-A UK'];
    return allowedFranchises.includes(selectedFranchise?.franchiseName || '');
  };

  const getAlertMessage = (): string => {
    if (isCustomMessageFranchise()) {
      return isSubmittedSuccessfully ? successMessage : errorMessage;
    } else {
      return isSubmittedSuccessfully
        ? t('successMessage')
        : `${t('errorMessage')} ${errorMessage}`;
    }
  };

  const rowsPerPage = 10;

  const maxPages = () => {
    return Math.ceil(totalItems / rowsPerPage);
  };

  useEffect(() => {
    if(isSubmittedSuccessfully) 
    {
      loadData(page, searchTerm);
    }
  }, [page, isSubmittedSuccessfully]);

  const onSearch = () => {
    setPage(0);
    loadData(0, searchTerm);
  };

  const loadData = (page: number, searchBy: string) => {
    setIsLoading(true);
    const totalSkip = page * rowsPerPage;
    if (selectedStore) {
      getRecipes(searchBy, selectedStore.storeNumber, totalSkip, rowsPerPage)
        .then((result) => {
          if (result?.data) {
            setRecipes(result.data);
            setTotalItems(result.totalCount ?? 0);
            setShowRecipeDialog(false);
          }
        })
        .catch((error) => {
          logError(error);
        })
        .finally(() => {
          setIsLoading(false);
          setLastSearchTerm(searchBy);
        });
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const onClear = () => {
    setPage(0);
    setSearchTerm('');
    loadData(0, '');
  };

  const handleOpenBulk = () => setOpenBulk(true);
  const handleCloseBulk = () => setOpenBulk(false);

  const onSubmit = (recipe: CreateMadeInStoreItemRequestDto) => {
    setIsUpdating(true);

    createMadeInStore(recipe)
      .then(() => {
        setIsSubmittedSuccessfully((prev) => prev + 1);
        setSuccessMessage('Recipe created successfully!');
      })
      .catch((error) => {
        logError(error);
        setErrorMessage(error);
        setIsSubmittedSuccessfully(0);
      })
      .finally(() => {
        setIsUpdating(false);
        setShowSubmitStatusMessage(true);
      });
  };


  const componentToUpsertComponent = (component: ItemComponent, itemId: number, storeNumber: string): UpsertItemComponent => {
    return {
      itemId: itemId,
      componentItemId: component.componentId,
      quantity: component.quantity,
      updateForStore: component.updateForStore,
      weight: component.weight,
      volume: component.volume,
      storeNumber: storeNumber,
    };
  };

  const onUpdate = async (
    entityId: number,
    entityType: MadeInStoreType,
    added: ItemComponent[],
    updated: ItemComponent[],
    deleted: ItemComponent[],
    updateForStore: boolean,
    storeNumber: string,
  ) => {

    setIsUpdating(true);

    try {

      const deletePromises = deleted.map((component) =>
        component.componentId
          ? deleteComponent(entityId, component.componentId, updateForStore)
          : Promise.resolve(),
      );

      const responses: Array<string | void> = [];
      for (const component of added) {
        const response = await addComponent(componentToUpsertComponent(component, entityId, storeNumber));
        responses.push(response);
      }

      for (const component of updated) {
        const updatedComponent = {
          ...component,
          updateForStore: updateForStore,
        };
        const response = await addComponent(componentToUpsertComponent(updatedComponent, entityId, storeNumber));
        responses.push(response);
      }

      const deleteResponses = await Promise.all([...deletePromises]);
      const responseMessage = [...responses, ...deleteResponses]
        .map((response) => (typeof response === 'string' ? JSON.parse(response) : response))
        .find((res) => res?.message)?.message || t('updateSuccessMessage');
      setSuccessMessage(responseMessage);
      setIsSubmittedSuccessfully((prev) => prev + 1);
      setShowRecipeDialog(false);
    } catch (error) {
      logError(error);
      const errorMessage = error != null ? error : t('updateErrorMessage');
      setErrorMessage(String(errorMessage));
      setIsSubmittedSuccessfully(0);
    } finally {
      setIsUpdating(false);
      setShowSubmitStatusMessage(true);
      loadData(page, searchTerm);
    }
  };

  const handleCloseMessage = () => {
    setShowUploadSuccessMessage(false);
    setShowSubmitStatusMessage(false);
  };

  return (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('search')}
          value={searchTerm}
          onChange={(event) => handleSearchChange(event.target.value)}
          onKeyDown={e => handleSearchFieldKeyDown(e, onSearch)}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button fullWidth
          onClick={onClear}
          variant='secondary'
          size='lg'
          sx={{ mr: 4 }}
        >
          {t('clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}>
          {t('search')}
        </Button>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <RecipeList
            handleOpenBulk={handleOpenBulk}
            list={recipes}
            totalCount={totalItems}
            currencyCode={currencyCode}
            isLoading={isLoading}
            onClear={onClear}
            onCreateNew={onCreateNew}
            lastSearchTerm={lastSearchTerm}
          />
          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={maxPages()}
            breakpointForChangeDisplay={120000}
            useNoDataMessage={false}
          ></CustomPagination>
        </Section>
      </Grid>
      <CreateRecipeDialog
        isUpdating={isUpdating}
        onCreateConfirm={onSubmit}
        onUpdateConfirm={onUpdate}
        isComplete={isSubmittedSuccessfully}
      />

      <CreateBulkManage
        open={openBulk}
        handleClose={handleCloseBulk}
        onSuccess={(message: string) => {
          setSuccessMessageBulk(message);
          setShowUploadSuccessMessage(true);

          setIsLoading(true);
          const totalSkip = page * rowsPerPage;
          if (selectedStore) {
            getRecipes(searchTerm, selectedStore.storeNumber, totalSkip, rowsPerPage)
              .then((result) => {
                if (result?.data) {
                  setRecipes(result.data);
                  setTotalItems(result.totalCount ?? 0);
                  setShowRecipeDialog(false);
                }
              })
              .catch(logError)
              .finally(() => setIsLoading(false));
          }
        }}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSubmitStatusMessage}
        onClose={() => setShowSubmitStatusMessage(false)}
        autoHideDuration={5000}
      >
        <Alert
          onClose={() => setShowSubmitStatusMessage(false)}
          severity={isSubmittedSuccessfully ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {getAlertMessage()}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default RecipesTab;